import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { scheduleAPI, serviceAPI, daysOffAPI, userPreferencesAPI } from '../services/apiWithAuth';

const useAppStore = create(
  devtools(
    persist(
      (set, get) => ({
  // Theme state
  darkMode: false,
  toggleTheme: () => set((state) => {
    const newDarkMode = !state.darkMode;
    return { darkMode: newDarkMode };
  }),
  
  // Initial loading state
  isInitializing: true,
  setInitializing: (isInitializing) => set({ isInitializing }),
  
  // Current employee (zalogowany pracownik)
  currentEmployee: null,
  setCurrentEmployee: (employee) => set({ currentEmployee: employee }),
  
  // Employees state
  employees: [],
  loadingEmployees: false,
  // Załaduj employeeOrder SYNCHRONICZNIE z localStorage przy inicjalizacji (przed renderem!)
  employeeOrder: (() => {
    try {
      return JSON.parse(localStorage.getItem('employeeOrder') || '[]');
    } catch {
      return [];
    }
  })(),
  employeeOrderLoaded: false, // Czy kolejność została załadowana z API
  setEmployees: (employees) => set({ employees }),
  setLoadingEmployees: (loading) => set({ loadingEmployees: loading }),
  
  // Load employee order from API (aktualizacja w tle, bez "flash")
  loadEmployeeOrder: async () => {
    const currentOrder = get().employeeOrder; // Już załadowana z localStorage przy inicjalizacji
    
    try {
      // Spróbuj zaktualizować z API jeśli użytkownik jest zalogowany
      const preferences = await userPreferencesAPI.get();
      const apiOrder = preferences.employee_order || [];
      
      // Aktualizuj tylko jeśli API ma dane (nie nadpisuj pustą tablicą z API)
      if (apiOrder.length > 0 && JSON.stringify(apiOrder) !== JSON.stringify(currentOrder)) {
        set({ employeeOrder: apiOrder });
        console.log('✅ Updated employee order from API:', apiOrder);
      } else {
        console.log('ℹ️ Employee order unchanged (API matched localStorage)');
      }
      
      set({ employeeOrderLoaded: true });
    } catch (error) {
      // Pozostaw aktualną kolejność (już załadowana z localStorage)
      set({ employeeOrderLoaded: true });
      
      if (error.response?.status === 401) {
        console.log('⚠️ User not authenticated - keeping localStorage order:', currentOrder);
      } else {
        console.error('⚠️ API error - keeping localStorage order:', error.message);
      }
    }
  },
  
  // Employee order management
  setEmployeeOrder: async (order) => {
    // Zawsze zapisz do localStorage (trwałe storage, nie cache!)
    localStorage.setItem('employeeOrder', JSON.stringify(order));
    set({ employeeOrder: order });
    console.log('✅ Saved employee order to localStorage:', order);
    
    // Spróbuj też zapisać do API jeśli użytkownik jest zalogowany
    try {
      await userPreferencesAPI.update({ employee_order: order });
      console.log('✅ Also saved employee order to API (user is authenticated)');
    } catch (error) {
      if (error.response?.status === 401) {
        console.log('⚠️ User not authenticated - using localStorage only');
      } else {
        console.error('⚠️ API error (using localStorage):', error);
      }
    }
  },
  
  moveEmployee: async (fromIndex, toIndex) => {
    const sortedEmployees = get().getSortedEmployees();
    const newOrder = [...sortedEmployees.map(emp => emp.code)];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    
    // Zapisz nową kolejność
    await get().setEmployeeOrder(newOrder);
  },
  
  getSortedEmployees: () => {
    const { employees, employeeOrder } = get();
    const activeEmployees = employees.filter(emp => emp.is_active);
    
    if (employeeOrder.length === 0) {
      return activeEmployees.sort((a, b) => a.code.localeCompare(b.code));
    }
    
    // Sortuj według zapisanej kolejności
    const sorted = [];
    const remaining = [...activeEmployees];
    
    // Najpierw dodaj pracowników w zapisanej kolejności
    for (const code of employeeOrder) {
      const empIndex = remaining.findIndex(emp => emp.code === code);
      if (empIndex !== -1) {
        sorted.push(remaining.splice(empIndex, 1)[0]);
      }
    }
    
    // Potem dodaj pozostałych (nowych) pracowników
    sorted.push(...remaining.sort((a, b) => a.code.localeCompare(b.code)));
    
    return sorted;
  },
  
  // Work types state
  workTypes: [],
  loadingWorkTypes: false,
  setWorkTypes: (workTypes) => set({ workTypes }),
  setLoadingWorkTypes: (loading) => set({ loadingWorkTypes: loading }),
  
  // Calendar state
  selectedDate: new Date(),
  currentYear: new Date().getFullYear(),
  currentMonth: new Date().getMonth() + 1,
  scheduleData: [],
  loadingSchedule: false,
  
  // Services state
  services: [], // Array of services for current month
  loadingServices: false,
  
  setServices: (services) => set({ services }),
  setLoadingServices: (loading) => set({ loadingServices: loading }),
  
  // Add/update/remove service helpers
  addService: (service) => set((state) => ({
    services: [...state.services, service]
  })),
  
  updateService: (updatedService) => set((state) => ({
    services: state.services.map(service => 
      service.year === updatedService.year && 
      service.month === updatedService.month && 
      service.day === updatedService.day 
        ? updatedService 
        : service
    )
  })),
  
  removeService: async (year, month, day, updatedBy = 'user') => {
    console.log(`🗑️ removeService called: ${year}-${month}-${day}`);
    
    // Check if service exists
    const existingService = get().getServiceForDate(year, month, day);
    console.log('Existing service:', existingService);
    
    if (!existingService) {
      console.log('No service found to remove');
      return;
    }
    
    // Remove from local state
    set((state) => ({
      services: state.services.filter(service => 
        !(service.year === year && service.month === month && service.day === day)
      )
    }));
    console.log('Service removed from local state');
    
    // Remove from database
    try {
      await serviceAPI.deleteService(year, month, day, updatedBy);
      console.log('Service removed from database');
    } catch (error) {
      console.error('Failed to delete service from database:', error);
      // Re-add to local state on error
      set((state) => ({
        services: [...state.services, existingService]
      }));
    }
  },
  
  // Get service for specific date
  getServiceForDate: (year, month, day) => {
    const { services } = get();
    return services.find(service => 
      service.year === year && service.month === month && service.day === day
    ) || null;
  },
  
  // Active work type for quick input
  activeWorkType: null, // Currently selected work type for quick input
  quickInputMode: false, // Whether quick input mode is enabled
  
  // Holidays state - now loaded from database
  holidays: [], // Array of holiday objects with structure from API
  holidaysLoaded: false, // Flag to track if holidays have been loaded
  loadingHolidays: false,
  holidayMode: false, // Whether holiday mode is enabled (for adding holidays by clicking day headers)
  
  // Load holidays from API
  loadHolidays: async (year) => {
    set({ loadingHolidays: true });
    try {
      const response = await daysOffAPI.getForYear(year);
      // Transform API response to match internal format
      const holidays = response.map(holiday => {
        if (holiday.recurring_month && holiday.recurring_day) {
          // Recurring holiday (appears every year)
          return {
            year: year, // Use current year for display
            month: holiday.recurring_month,
            day: holiday.recurring_day,
            name: holiday.name,
            id: holiday.id,
            recurring: true
          };
        } else if (holiday.specific_date) {
          // Specific date holiday
          const date = new Date(holiday.specific_date);
          return {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate(),
            name: holiday.name,
            id: holiday.id,
            recurring: false
          };
        }
        return null;
      }).filter(h => h !== null);
      
      set({ holidays, holidaysLoaded: true, loadingHolidays: false });
    } catch (error) {
      console.error('Error loading holidays:', error);
      set({ loadingHolidays: false });
    }
  },
  
  setSelectedDate: (date) => set({ 
    selectedDate: date,
    currentYear: date.getFullYear(),
    currentMonth: date.getMonth() + 1
  }),
  setActiveWorkType: (workType) => set({ 
    activeWorkType: workType,
    quickInputMode: !!workType
  }),
  clearActiveWorkType: () => set({ 
    activeWorkType: null,
    quickInputMode: false
  }),
  
  // Holiday management functions
  setHolidayMode: (enabled) => set({ 
    holidayMode: enabled 
  }),
  
  toggleHolidayMode: () => set((state) => ({ 
    holidayMode: !state.holidayMode 
  })),
  
  addHoliday: async (year, month, day, name = 'Święto', recurring = true) => {
    try {
      if (recurring) {
        // Create as recurring holiday (appears every year)
        await daysOffAPI.create({
          name,
          recurring_month: month,
          recurring_day: day,
          day_type: 'holiday',
          is_active: true
        });
      } else {
        // Create as one-time holiday for specific year
        const specificDate = new Date(year, month - 1, day);
        await daysOffAPI.create({
          name,
          specific_date: specificDate.toISOString().split('T')[0],
          day_type: 'holiday',
          is_active: true
        });
      }
      
      // Reload holidays for current year
      await get().loadHolidays(year);
      return true;
    } catch (error) {
      console.error('Error adding holiday:', error);
      return false;
    }
  },
  
  removeHoliday: async (year, month, day) => {
    try {
      const { holidays } = get();
      const holiday = holidays.find(h => 
        h.year === year && h.month === month && h.day === day
      );
      
      if (holiday && holiday.id) {
        await daysOffAPI.delete(holiday.id);
        // Reload holidays for current year
        await get().loadHolidays(year);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing holiday:', error);
      return false;
    }
  },
  
  isHoliday: (year, month, day) => {
    const { holidays } = get();
    return holidays.some(h => 
      h.month === month && h.day === day &&
      (h.recurring || h.year === year)
    );
  },
  
  getHoliday: (year, month, day) => {
    const { holidays } = get();
    return holidays.find(h => 
      h.month === month && h.day === day &&
      (h.recurring || h.year === year)
    ) || null;
  },
  
  // Get today's service
  getTodaysService: () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    return get().getServiceForDate(year, month, day);
  },
  setCurrentDate: (year, month) => set({ currentYear: year, currentMonth: month }),
  setScheduleData: (scheduleData) => set({ scheduleData }),
  setLoadingSchedule: (loading) => set({ loadingSchedule: loading }),
  
  // Selected cell for editing
  selectedCell: null,
  setSelectedCell: (cell) => set({ selectedCell: cell }),
  clearSelectedCell: () => set({ selectedCell: null }),

  // Cell selection for copy/paste operations
  selectionMode: false, // Whether selection mode is active
  selectedCells: new Set(), // Set of selected cell keys (format: "employeeCode_day")
  copiedCells: [], // Array of copied cell data
  cutMode: false, // Whether copied cells should be deleted after paste
  
  // Selection mode functions
  toggleSelectionMode: () => set((state) => {
    const newSelectionMode = !state.selectionMode;
    return {
      selectionMode: newSelectionMode,
      selectedCells: new Set(), // Clear selection when toggling mode
      // Only clear cut mode when DISABLING selection mode
      ...(newSelectionMode ? {} : { cutMode: false, copiedCells: [] })
    };
  }),
  
  setSelectionMode: (enabled) => set((state) => ({
    selectionMode: enabled,
    selectedCells: new Set(),
    // Only clear cut mode when DISABLING selection mode
    ...(enabled ? {} : { cutMode: false, copiedCells: [] })
  })),
  
  // Cell selection functions
  selectCell: (employeeCode, day) => set((state) => {
    const cellKey = `${employeeCode}_${day}`;
    const newSelectedCells = new Set(state.selectedCells);
    newSelectedCells.add(cellKey);
    return { selectedCells: newSelectedCells };
  }),
  
  deselectCell: (employeeCode, day) => set((state) => {
    const cellKey = `${employeeCode}_${day}`;
    const newSelectedCells = new Set(state.selectedCells);
    newSelectedCells.delete(cellKey);
    return { selectedCells: newSelectedCells };
  }),
  
  toggleCellSelection: (employeeCode, day) => set((state) => {
    const cellKey = `${employeeCode}_${day}`;
    const newSelectedCells = new Set(state.selectedCells);
    
    if (newSelectedCells.has(cellKey)) {
      newSelectedCells.delete(cellKey);
    } else {
      newSelectedCells.add(cellKey);
    }
    
    return { selectedCells: newSelectedCells };
  }),
  
  selectCellRange: (startEmployeeCode, startDay, endEmployeeCode, endDay) => set((state) => {
    const employees = state.getSortedEmployees();
    const startEmpIndex = employees.findIndex(emp => emp.code === startEmployeeCode);
    const endEmpIndex = employees.findIndex(emp => emp.code === endEmployeeCode);
    
    const minEmpIndex = Math.min(startEmpIndex, endEmpIndex);
    const maxEmpIndex = Math.max(startEmpIndex, endEmpIndex);
    const minDay = Math.min(startDay, endDay);
    const maxDay = Math.max(startDay, endDay);
    
    const newSelectedCells = new Set(state.selectedCells);
    
    // Helper function to check if day is weekend
    const isWeekend = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth();
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
    };
    
    // Helper function to check if day is holiday
    const isHoliday = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth() + 1;
      return state.isHoliday(year, month, day);
    };
    
    for (let empIndex = minEmpIndex; empIndex <= maxEmpIndex; empIndex++) {
      const employeeCode = employees[empIndex].code;
      for (let day = minDay; day <= maxDay; day++) {
        // Skip weekends and holidays
        if (!isWeekend(day) && !isHoliday(day)) {
          const cellKey = `${employeeCode}_${day}`;
          newSelectedCells.add(cellKey);
        }
      }
    }
    
    return { selectedCells: newSelectedCells };
  }),
  
  clearSelection: () => set({
    selectedCells: new Set(),
    // Don't reset cutMode here - it should persist until paste or manual clear
  }),
  
  // Clear cut mode and clipboard
  clearCutMode: () => set({
    cutMode: false,
    copiedCells: []
  }),
  
  isCellSelected: (employeeCode, day) => {
    const { selectedCells } = get();
    const cellKey = `${employeeCode}_${day}`;
    return selectedCells.has(cellKey);
  },
  
  // Check if cell is in cut mode (for visual styling)
  isCellCut: (employeeCode, day) => {
    const { cutMode, copiedCells } = get();
    if (!cutMode || copiedCells.length === 0) return false;
    
    return copiedCells.some(cell => 
      cell.employeeCode === employeeCode && cell.day === parseInt(day)
    );
  },
  
  // Database helper functions for clipboard operations
  saveCellToDatabase: async (employeeCode, day, cellData, updatedBy = 'user') => {
    const state = get();
    const year = state.currentYear;
    const month = state.currentMonth;
    
    try {
      if (cellData) {
        // Create or update cell
        const scheduleEntry = {
          employee_code: employeeCode,
          year,
          month,
          day,
          work_type_code: cellData.work_type_code || cellData.work_type || '',
          custom_text: cellData.custom_text || '',
          hours: cellData.hours || '',
          updated_by: updatedBy
        };
        
        // Check if cell already exists in database
        const existingCell = state.getCellData(employeeCode, day);
        if (existingCell) {
          await scheduleAPI.updateSchedule(scheduleEntry);
        } else {
          await scheduleAPI.createSchedule(scheduleEntry);
        }
      } else {
        // Delete cell - handle 404 gracefully
        try {
          await scheduleAPI.deleteSchedule(employeeCode, year, month, day, updatedBy);
        } catch (error) {
          // Ignore 404 errors - cell already doesn't exist
          if (error.response?.status !== 404) {
            throw error;
          }
        }
      }
    } catch (error) {
      console.error(`Error saving cell to database: ${employeeCode} day ${day}`, error);
      throw error;
    }
  },
  
  // Clipboard functions
  copySelectedCells: () => set((state) => {
    // Helper function to check if day is weekend
    const isWeekend = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth();
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
    };
    
    // Helper function to check if day is holiday
    const isHoliday = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth() + 1;
      return state.isHoliday(year, month, day);
    };
    
    const copiedData = [];
    
    state.selectedCells.forEach(cellKey => {
      const [employeeCode, day] = cellKey.split('_');
      const dayNum = parseInt(day);
      
      // Skip weekends and holidays
      if (!isWeekend(dayNum) && !isHoliday(dayNum)) {
        const cellData = state.getCellData(employeeCode, dayNum);
        
        copiedData.push({
          employeeCode,
          day: dayNum,
          cellData: cellData ? { ...cellData } : null
        });
      }
    });
    
    return {
      copiedCells: copiedData,
      cutMode: false
    };
  }),
  
  cutSelectedCells: () => set((state) => {
    // Helper function to check if day is weekend
    const isWeekend = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth();
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
    };
    
    // Helper function to check if day is holiday
    const isHoliday = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth() + 1;
      return state.isHoliday(year, month, day);
    };
    
    const copiedData = [];
    
    state.selectedCells.forEach(cellKey => {
      const [employeeCode, day] = cellKey.split('_');
      const dayNum = parseInt(day);
      
      // Skip weekends and holidays
      if (!isWeekend(dayNum) && !isHoliday(dayNum)) {
        const cellData = state.getCellData(employeeCode, dayNum);
        
        copiedData.push({
          employeeCode,
          day: dayNum,
          cellData: cellData ? { ...cellData } : null
        });
      }
    });
    
    return {
      copiedCells: copiedData,
      cutMode: true
    };
  }),
  
  pasteToSelectedCells: async () => {
    const state = get();
    const { copiedCells, cutMode, selectedCells } = state;
    
    if (copiedCells.length === 0) return;
    
    // Helper function to check if day is weekend
    const isWeekend = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth();
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
    };
    
    // Helper function to check if day is holiday
    const isHoliday = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth() + 1;
      return state.isHoliday(year, month, day);
    };
    
    // Filter out weekend and holiday cells from selection
    const validSelectedCells = Array.from(selectedCells).filter(cellKey => {
      const [, day] = cellKey.split('_');
      const dayNum = parseInt(day);
      return !isWeekend(dayNum) && !isHoliday(dayNum);
    });
    
    if (validSelectedCells.length === 0) {
      get().setNotification({
        type: 'warning',
        message: 'Nie można wkleić do weekendów ani świąt. Zaznacz komórki w dni robocze.'
      });
      return;
    }

    // Group all paste operations into one undo operation
    const actions = [];
    
    // Function to handle pasting to a single cell
    const pasteToCell = (cellKey, copiedCell) => {
      const [employeeCode, day] = cellKey.split('_');
      const dayNum = parseInt(day);
      
      // Get current values before pasting for undo
      const currentCellData = get().getCellData(employeeCode, dayNum);
      const currentServiceData = get().getServiceForDate(state.currentYear, state.currentMonth, dayNum);
      
      // Record schedule change for undo
      if (copiedCell.cellData || currentCellData) {
        actions.push({
          type: copiedCell.cellData ? 'schedule_update' : 'schedule_delete',
          timestamp: new Date().toISOString(),
          data: {
            employeeCode,
            day: dayNum,
            month: state.currentMonth,
            year: state.currentYear,
            oldValue: currentCellData ? { ...currentCellData } : null,
            newValue: copiedCell.cellData ? { ...copiedCell.cellData } : null
          },
          description: `${copiedCell.cellData ? 'Wklejono do' : 'Usunięto'} komórki ${employeeCode} - ${dayNum}.${state.currentMonth.toString().padStart(2, '0')}.${state.currentYear}`
        });
      }
      
      // If pasting empty cell, remove any service assignment
      if (!copiedCell.cellData && currentServiceData) {
        actions.push({
          type: 'service_delete',
          timestamp: new Date().toISOString(),
          data: {
            employeeCode,
            day: dayNum,
            month: state.currentMonth,
            year: state.currentYear,
            oldValue: { ...currentServiceData },
            newValue: null
          },
          description: `Usunięto serwis ${employeeCode} - ${dayNum}.${state.currentMonth.toString().padStart(2, '0')}.${state.currentYear}`
        });
      }
    };
    
    // If we have exactly the same number of selected cells as copied cells,
    // paste one-to-one mapping
    if (validSelectedCells.length === copiedCells.length) {
      validSelectedCells.forEach((cellKey, index) => {
        const copiedCell = copiedCells[index];
        pasteToCell(cellKey, copiedCell);
      });
    } else {
      // Pattern-based pasting: repeat the copied pattern across selected cells
      validSelectedCells.forEach((cellKey, index) => {
        const copiedCell = copiedCells[index % copiedCells.length];
        pasteToCell(cellKey, copiedCell);
      });
    }

    // If cut mode, add deletion actions for source cells
    if (cutMode) {
      copiedCells.forEach(copiedCell => {
        if (!isWeekend(copiedCell.day) && !isHoliday(copiedCell.day)) {
          const currentCellData = get().getCellData(copiedCell.employeeCode, copiedCell.day);
          const currentServiceData = get().getServiceForDate(state.currentYear, state.currentMonth, copiedCell.day);
          
          // Record deletion of source cell for undo
          if (currentCellData) {
            actions.push({
              type: 'schedule_delete',
              timestamp: new Date().toISOString(),
              data: {
                employeeCode: copiedCell.employeeCode,
                day: copiedCell.day,
                month: state.currentMonth,
                year: state.currentYear,
                oldValue: { ...currentCellData },
                newValue: null
              },
              description: `Wycięto komórkę ${copiedCell.employeeCode} - ${copiedCell.day}.${state.currentMonth.toString().padStart(2, '0')}.${state.currentYear}`
            });
          }
          
          // Record deletion of source service for undo
          if (currentServiceData && currentServiceData.employee_code === copiedCell.employeeCode) {
            actions.push({
              type: 'service_delete',
              timestamp: new Date().toISOString(),
              data: {
                employeeCode: copiedCell.employeeCode,
                day: copiedCell.day,
                month: state.currentMonth,
                year: state.currentYear,
                oldValue: { ...currentServiceData },
                newValue: null
              },
              description: `Wycięto serwis ${copiedCell.employeeCode} - ${copiedCell.day}.${state.currentMonth.toString().padStart(2, '0')}.${state.currentYear}`
            });
          }
        }
      });
    }

    // Add grouped action to undo stack if we have actions
    if (actions.length > 0) {
      const groupedAction = {
        type: cutMode ? 'bulk_move' : 'bulk_paste',
        timestamp: new Date().toISOString(),
        data: {
          actions: actions,
          cellCount: validSelectedCells.length
        },
        description: `${cutMode ? 'Przeniesiono' : 'Wklejono'} ${validSelectedCells.length} komórek`
      };
      
      get().addToUndoStack(groupedAction);
    }
    
    // Actually perform the paste operations and save to database
    try {
      // Collect all API operations to execute in parallel
      const apiOperations = [];
      
      if (validSelectedCells.length === copiedCells.length) {
        // One-to-one mapping
        for (let index = 0; index < validSelectedCells.length; index++) {
          const cellKey = validSelectedCells[index];
          const [employeeCode, day] = cellKey.split('_');
          const copiedCell = copiedCells[index];
          const dayNum = parseInt(day);
          
          // Update local state first
          if (copiedCell.cellData) {
            console.log(`📋 Pasting data to ${employeeCode}_${dayNum}:`, copiedCell.cellData);
            get().updateScheduleCell(employeeCode, dayNum, { ...copiedCell.cellData }, false);
          } else {
            // When pasting empty cell, also remove service
            console.log(`🗑️ Pasting empty cell to ${employeeCode}_${dayNum}, removing service`);
            get().updateScheduleCell(employeeCode, dayNum, null, false);
            await get().removeService(state.currentYear, state.currentMonth, dayNum);
          }
          
          // Collect API operation for parallel execution
          apiOperations.push(get().saveCellToDatabase(employeeCode, dayNum, copiedCell.cellData));
        }
      } else {
        // Pattern-based pasting
        for (let index = 0; index < validSelectedCells.length; index++) {
          const cellKey = validSelectedCells[index];
          const [employeeCode, day] = cellKey.split('_');
          const copiedCell = copiedCells[index % copiedCells.length];
          const dayNum = parseInt(day);
          
          // Update local state first
          if (copiedCell.cellData) {
            get().updateScheduleCell(employeeCode, dayNum, { ...copiedCell.cellData }, false);
          } else {
            // When pasting empty cell, also remove service
            get().updateScheduleCell(employeeCode, dayNum, null, false);
            await get().removeService(state.currentYear, state.currentMonth, dayNum);
          }
          
          // Collect API operation for parallel execution
          apiOperations.push(get().saveCellToDatabase(employeeCode, dayNum, copiedCell.cellData));
        }
      }
      
      // If cut mode, delete original cells (but only non-weekend and non-holiday cells)
      if (cutMode) {
        for (const copiedCell of copiedCells) {
          if (!isWeekend(copiedCell.day) && !isHoliday(copiedCell.day)) {
            // Update local state first
            get().updateScheduleCell(copiedCell.employeeCode, copiedCell.day, null, false);
            await get().removeService(state.currentYear, state.currentMonth, copiedCell.day);
            
            // Collect API operation for parallel execution
            apiOperations.push(get().saveCellToDatabase(copiedCell.employeeCode, copiedCell.day, null));
          }
        }
        
        // Cut mode - clear clipboard after pasting (one-time operation)
        // Also clear selection from the original cut cells
        const newSelectedCells = new Set(get().selectedCells);
        copiedCells.forEach(copiedCell => {
          const cellKey = `${copiedCell.employeeCode}_${copiedCell.day}`;
          newSelectedCells.delete(cellKey);
        });
        
        set({ 
          cutMode: false, 
          copiedCells: [],
          selectedCells: newSelectedCells
        });
      } else {
        // Copy mode - keep clipboard for multiple pastes (don't clear copiedCells)
        // Don't clear copiedCells for copy operations
      }
      
      // Execute all API operations in parallel
      await Promise.all(apiOperations);
      
      // Show success notification
      get().setNotification({
        type: 'success',
        message: `${cutMode ? 'Przeniesiono' : 'Wklejono'} ${validSelectedCells.length} komórek`
      });
      
    } catch (error) {
      console.error('Error during paste operation:', error);
      get().setNotification({
        type: 'error',
        message: `Błąd podczas ${cutMode ? 'przenoszenia' : 'wklejania'} komórek`
      });
    }
  },
  
  deleteSelectedCells: async () => {
    const state = get();
    const { selectedCells } = state;
    
    // Helper function to check if day is weekend
    const isWeekend = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth();
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
    };
    
    // Helper function to check if day is holiday
    const isHoliday = (day) => {
      const year = state.selectedDate.getFullYear();
      const month = state.selectedDate.getMonth() + 1;
      return state.isHoliday(year, month, day);
    };
    
    // Filter out weekend and holiday cells from selection
    const validSelectedCells = Array.from(selectedCells).filter(cellKey => {
      const [, day] = cellKey.split('_');
      const dayNum = parseInt(day);
      return !isWeekend(dayNum) && !isHoliday(dayNum);
    });
    
    if (validSelectedCells.length === 0) {
      get().setNotification({
        type: 'warning',
        message: 'Nie można usuwać weekendów ani świąt. Zaznacz komórki w dni robocze.'
      });
      return;
    }

    // Group all deletions into one undo operation
    const actions = [];
    
    validSelectedCells.forEach(cellKey => {
      const [employeeCode, day] = cellKey.split('_');
      const dayNum = parseInt(day);
      
      // Get current values before deletion for undo
      const cellData = get().getCellData(employeeCode, dayNum);
      const serviceData = get().getServiceForDate(state.currentYear, state.currentMonth, dayNum);
      
      // Delete schedule cell (work type, hours, etc.)
      if (cellData) {
        actions.push({
          type: 'schedule_delete',
          timestamp: new Date().toISOString(),
          data: {
            employeeCode,
            day: dayNum,
            month: state.currentMonth,
            year: state.currentYear,
            oldValue: { ...cellData },
            newValue: null
          },
          description: `Usunięto komórkę ${employeeCode} - ${dayNum}.${state.currentMonth.toString().padStart(2, '0')}.${state.currentYear}`
        });
      }
      
      // Delete service if exists
      if (serviceData && serviceData.employee_code === employeeCode) {
        actions.push({
          type: 'service_delete',
          timestamp: new Date().toISOString(),
          data: {
            employeeCode,
            day: dayNum,
            month: state.currentMonth,
            year: state.currentYear,
            oldValue: { ...serviceData },
            newValue: null
          },
          description: `Usunięto serwis ${employeeCode} - ${dayNum}.${state.currentMonth.toString().padStart(2, '0')}.${state.currentYear}`
        });
      }
    });

    // Add grouped action to undo stack if we have actions
    if (actions.length > 0) {
      const groupedAction = {
        type: 'bulk_delete',
        timestamp: new Date().toISOString(),
        data: {
          actions: actions,
          cellCount: validSelectedCells.length
        },
        description: `Usunięto ${validSelectedCells.length} komórek`
      };
      
      get().addToUndoStack(groupedAction);
    }
    
    // Actually perform the deletions and save to database
    try {
      // Collect all API operations to execute in parallel
      const apiOperations = [];
      
      for (const cellKey of validSelectedCells) {
        const [employeeCode, day] = cellKey.split('_');
        const dayNum = parseInt(day);
        
        // Update local state first
        get().updateScheduleCell(employeeCode, dayNum, null, false);
        await get().removeService(state.currentYear, state.currentMonth, dayNum);
        
        // Collect API operation for parallel execution
        apiOperations.push(get().saveCellToDatabase(employeeCode, dayNum, null));
      }
      
      // Execute all API operations in parallel
      await Promise.all(apiOperations);
      
      get().setNotification({
        type: 'success',
        message: `Usunięto ${validSelectedCells.length} komórek`
      });
      
    } catch (error) {
      console.error('Error during delete operation:', error);
      get().setNotification({
        type: 'error',
        message: 'Błąd podczas usuwania komórek'
      });
    }
  },
  
  // Notifications
  notification: null,
  setNotification: (notification) => set({ notification }),
  clearNotification: () => set({ notification: null }),

  // ChangeLog state
  changeLogHistory: [],
  loadingChangeLog: false,
  setChangeLogHistory: (history) => set({ changeLogHistory: history }),
  setLoadingChangeLog: (loading) => set({ loadingChangeLog: loading }),
  
  // Add changelog entry
  addChangeLogEntry: (entry) => set((state) => ({
    changeLogHistory: [entry, ...state.changeLogHistory]
  })),

  // Undo/Redo functionality
  undoStack: [],
  redoStack: [],
  maxUndoStackSize: 50, // Limit undo history to prevent memory issues

  // Add action to undo stack
  addToUndoStack: (action) => set((state) => {
    console.log('Adding action to undo stack:', action);
    const newUndoStack = [...state.undoStack];
    
    // Add new action to beginning of stack
    newUndoStack.unshift(action);
    
    // Limit stack size
    if (newUndoStack.length > state.maxUndoStackSize) {
      newUndoStack.pop();
    }
    
    console.log('Undo stack length:', newUndoStack.length);
    
    return {
      undoStack: newUndoStack,
      redoStack: [] // Clear redo stack when new action is performed
    };
  }),

  // Check if undo is possible
  canUndo: () => {
    const { undoStack } = get();
    return undoStack.length > 0;
  },

  // Check if redo is possible
  canRedo: () => {
    const { redoStack } = get();
    return redoStack.length > 0;
  },

  // Execute undo operation
  undo: async () => {
    const state = get();
    console.log('Store undo called, stack length:', state.undoStack.length);
    if (state.undoStack.length === 0) {
      console.log('No actions to undo');
      return;
    }
    
    const [actionToUndo, ...remainingUndoStack] = state.undoStack;
    const newRedoStack = [actionToUndo, ...state.redoStack];
    
    console.log('Undoing action:', actionToUndo);
    
    // Update state first
    set({
      undoStack: remainingUndoStack,
      redoStack: newRedoStack
    });
    
    // Execute the undo action
    await get().executeUndoAction(actionToUndo);
  },

  // Execute redo operation
  redo: async () => {
    const state = get();
    console.log('Store redo called, stack length:', state.redoStack.length);
    if (state.redoStack.length === 0) {
      console.log('No actions to redo');
      return;
    }
    
    const [actionToRedo, ...remainingRedoStack] = state.redoStack;
    const newUndoStack = [actionToRedo, ...state.undoStack];
    
    console.log('Redoing action:', actionToRedo);
    
    // Update state first
    set({
      undoStack: newUndoStack,
      redoStack: remainingRedoStack
    });
    
    // Execute the redo action
    await get().executeRedoAction(actionToRedo);
  },

  // Clear undo/redo stacks
  clearUndoRedoStacks: () => set({
    undoStack: [],
    redoStack: []
  }),

  // Execute undo action based on action type
  executeUndoAction: async (action) => {
    const { type, data } = action;
    console.log('Executing undo action:', type, data);
    
    switch (type) {
      case 'bulk_delete':
      case 'bulk_paste':
      case 'bulk_move':
        // Execute all sub-actions in reverse order
        if (data.actions && Array.isArray(data.actions)) {
          for (const subAction of [...data.actions].reverse()) {
            await get().executeUndoAction(subAction);
          }
        }
        break;
        
      case 'schedule_update':
      case 'schedule_delete':
        // Restore previous schedule cell value
        if (data.oldValue !== null) {
          get().updateScheduleCell(data.employeeCode, data.day, data.oldValue, false);
          await get().saveCellToDatabase(data.employeeCode, data.day, data.oldValue);
        } else {
          // If old value was null, remove the cell
          get().updateScheduleCell(data.employeeCode, data.day, null, false);
          await get().saveCellToDatabase(data.employeeCode, data.day, null);
        }
        break;
        
      case 'schedule_create':
        // Remove the created schedule cell
        get().updateScheduleCell(data.employeeCode, data.day, null, false);
        await get().saveCellToDatabase(data.employeeCode, data.day, null);
        break;

      case 'service_update':
      case 'service_delete':
        // Restore previous service data
        if (data.oldValue !== null) {
          get().addService(data.oldValue);
        } else {
          // If old value was null, remove the service
          await get().removeService(data.year, data.month, data.day);
        }
        break;
        
      case 'service_create':
        // Remove the created service
        await get().removeService(data.year, data.month, data.day);
        break;
        
      case 'employee_update':
        // Restore previous employee data
        if (data.oldEmployee) {
          set((state) => ({
            employees: state.employees.map(emp => 
              emp.code === data.employeeCode ? data.oldEmployee : emp
            )
          }));
        }
        break;
        
      case 'employee_create':
        // Remove the created employee
        set((state) => ({
          employees: state.employees.filter(emp => emp.code !== data.employeeCode)
        }));
        break;
        
      case 'employee_delete':
        // Restore the deleted employee
        if (data.oldEmployee) {
          set((state) => ({
            employees: [...state.employees, data.oldEmployee]
          }));
        }
        break;
        
      default:
        console.warn('Unknown undo action type:', type);
    }
  },

  // Execute redo action based on action type
  executeRedoAction: async (action) => {
    const { type, data } = action;
    console.log('Executing redo action:', type, data);
    
    switch (type) {
      case 'bulk_delete':
      case 'bulk_paste':
      case 'bulk_move':
        // Execute all sub-actions in original order (not reversed like undo)
        if (data.actions && Array.isArray(data.actions)) {
          for (const subAction of data.actions) {
            await get().executeRedoAction(subAction);
          }
        }
        break;
        
      case 'schedule_update':
      case 'schedule_create':
        // Restore the new schedule cell value
        if (data.newValue !== null) {
          get().updateScheduleCell(data.employeeCode, data.day, data.newValue, false);
          await get().saveCellToDatabase(data.employeeCode, data.day, data.newValue);
        } else {
          get().updateScheduleCell(data.employeeCode, data.day, null, false);
          await get().saveCellToDatabase(data.employeeCode, data.day, null);
        }
        break;
        
      case 'schedule_delete':
        // Remove the schedule cell again
        get().updateScheduleCell(data.employeeCode, data.day, null, false);
        await get().saveCellToDatabase(data.employeeCode, data.day, null);
        break;

      case 'service_update':
      case 'service_create':
        // Restore the new service data
        if (data.newValue !== null) {
          get().addService(data.newValue);
        } else {
          await get().removeService(data.year, data.month, data.day);
        }
        break;
        
      case 'service_delete':
        // Remove the service again
        await get().removeService(data.year, data.month, data.day);
        break;
        
      case 'employee_update':
      case 'employee_create':
        // Restore the new employee data
        if (data.newEmployee) {
          if (type === 'employee_create') {
            set((state) => ({
              employees: [...state.employees, data.newEmployee]
            }));
          } else {
            set((state) => ({
              employees: state.employees.map(emp => 
                emp.code === data.employeeCode ? data.newEmployee : emp
              )
            }));
          }
        }
        break;
        
      case 'employee_delete':
        // Remove the employee again
        set((state) => ({
          employees: state.employees.filter(emp => emp.code !== data.employeeCode)
        }));
        break;
        
      default:
        console.warn('Unknown redo action type:', type);
    }
  },
  
  // Actions
  addEmployee: (employee) => set((state) => ({
    employees: [...state.employees, employee]
  })),
  
  updateEmployee: (updatedEmployee) => set((state) => ({
    employees: state.employees.map(emp => 
      emp.code === updatedEmployee.code ? updatedEmployee : emp
    )
  })),
  
  removeEmployee: (employeeCode) => set((state) => ({
    employees: state.employees.filter(emp => emp.code !== employeeCode)
  })),

  // Schedule actions
  addScheduleEntry: (entry) => set((state) => ({
    scheduleData: [...state.scheduleData, entry]
  })),

  updateScheduleEntry: (updatedEntry) => set((state) => ({
    scheduleData: state.scheduleData.map(entry => 
      entry.id === updatedEntry.id ? updatedEntry : entry
    )
  })),

  removeScheduleEntry: (entryId) => set((state) => ({
    scheduleData: state.scheduleData.filter(entry => entry.id !== entryId)
  })),
  
  updateScheduleCell: (employeeCode, day, cellData, recordForUndo = true) => {
    const state = get();
    const newScheduleData = { ...state.scheduleData };
    
    // Find employee in schedule data
    const employeeIndex = newScheduleData.employees?.findIndex(
      emp => emp.code === employeeCode
    );
    
    let oldValue = null;
    
    if (employeeIndex !== -1) {
      // Get old value for undo/redo
      oldValue = newScheduleData.employees[employeeIndex].schedule[day] || null;
      
      // Update existing employee schedule
      newScheduleData.employees[employeeIndex].schedule[day] = cellData;
    }
    
    // Record action for undo/redo if needed and values are different
    if (recordForUndo && JSON.stringify(oldValue) !== JSON.stringify(cellData)) {
      const action = {
        type: cellData === null ? 'schedule_delete' : 
              oldValue === null ? 'schedule_create' : 'schedule_update',
        timestamp: new Date().toISOString(),
        data: {
          employeeCode,
          day,
          month: state.currentMonth,
          year: state.currentYear,
          oldValue: oldValue ? { ...oldValue } : null,
          newValue: cellData ? { ...cellData } : null
        },
        description: `${cellData === null ? 'Usunięto' : 
                      oldValue === null ? 'Dodano' : 'Zmieniono'} komórkę ${employeeCode} - ${day}.${state.currentMonth.toString().padStart(2, '0')}.${state.currentYear}`
      };
      
      get().addToUndoStack(action);
    }
    
    // Update the schedule data
    set({ scheduleData: newScheduleData });
  },
  
  // Navigation helpers
  navigateMonth: (direction) => set((state) => {
    let newMonth = state.currentMonth;
    let newYear = state.currentYear;
    
    if (direction === 'next') {
      newMonth++;
      if (newMonth > 12) {
        newMonth = 1;
        newYear++;
      }
    } else {
      newMonth--;
      if (newMonth < 1) {
        newMonth = 12;
        newYear--;
      }
    }
    
    return { currentMonth: newMonth, currentYear: newYear };
  }),
  
  // Utility functions
  getEmployeeSchedule: (employeeCode) => {
    const { scheduleData } = get();
    const employee = scheduleData.employees?.find(emp => emp.code === employeeCode);
    return employee?.schedule || {};
  },
  
  getCellData: (employeeCode, day) => {
    const schedule = get().getEmployeeSchedule(employeeCode);
    return schedule[day] || null;
  }
}),
      {
        name: 'kalendarz-pracy-store',
        // Persist only important data
        // employeeOrder is now stored in database via API, not in localStorage
        partialize: (state) => ({
          darkMode: state.darkMode,
          employees: state.employees,
          workTypes: state.workTypes,
        }),
      }
    )
  )
);

export default useAppStore;